import { Float32BufferAttribute, BufferGeometry, Color, Points, PointsMaterial, Vector3 } from "three"
import { Utils } from "/src/scripts/utils"
import { ChapterPositions } from "./chapters"

export { Vertices }

class Vertices extends Points {
    #chapterPositions
    #clock
    #count
    #ease = Utils.easeInOut(1.7)
    #cooldowns = []

    /**
     * Updates the postion of a vertex by a delta vector
     * @param {number} index 
     * @param {import("three").Vector3} positionDelta vector to update position 
     * @returns {import("three").Vector3} new vertex position
     */
    #update(index, postionDelta) {
        const positionAttribute = this.geometry.getAttribute("position")
        const currentPosition = new Vector3().fromBufferAttribute(positionAttribute, index)
        const newPosition = new Vector3().addVectors(currentPosition, postionDelta)

        positionAttribute.setXYZ(index, ...newPosition)
        positionAttribute.needsUpdate = true

        return newPosition
    }

    /**
     * Creates a vector with a positions 0 (50 % of the cases)
     * or a number between -0.5 and +0.5 (50% of the cases)
     * for every Vector3 dimension
     * @returns {import("three").Vector3}
     */
    #newPosition() {
        const amplitude = .4
        const getDimension = () =>
            // pseudo-random numbers are fine here as they
            // are only used to create a new position fixture.
            // eslint-disable-next-line sonarjs/pseudo-random
            Math.random() < .5 ? Utils.basicRandomValue() * amplitude : 0

        return new Vector3(...Array.from({ length: 3 }, getDimension))
    }

    /**
     * Calculates the difference between the current position and 
     * the new position vector
     * @param {number} index 
     * @param {import("three").Vector3} newPosition for the vertex
     * @returns {import("three").Vector3}
     */
    #positionDifference(index, newPosition) {
        const positionAttribute = this.geometry.getAttribute("position")
        const currentPosition = new Vector3().fromBufferAttribute(positionAttribute, index)
        const deltaVector = new Vector3().subVectors(newPosition, currentPosition)

        return deltaVector
    }

    static #getColour() {
        const colour = new Color()
        // pseudo-random numbers are fine here as they
        // are only used to select on of colours
        // eslint-disable-next-line sonarjs/pseudo-random
        const gradient = Math.random() < .12
            // 10 % case: one of the brand colours
            // eslint-disable-next-line sonarjs/pseudo-random
            ? Math.ceil(Math.random() * 3)
            // 90 % case: a greyscale colour
            : 0

        switch (gradient) {
            case 1:
                colour.setRGB(6 / 255, 100 / 255, 162 / 255)
                break
            case 2:
                colour.setRGB(18 / 255, 147 / 255, 199 / 255)
                break
            case 3:
                colour.setRGB(15 / 255, 178 / 255, 195 / 255)
                break
            default:
                // pseudo-random numbers are fine here as they
                // are only used to select a random colour level.
                // eslint-disable-next-line sonarjs/pseudo-random
                colour.setHSL(0, 0, Math.random() * .5 + .25)
        }

        return colour
    }

    static #colours(count) {
        const colours = []

        for (let i = 0; i < count; i++) {
            const colour = Vertices.#getColour()
            colours.push(colour.r, colour.g, colour.b)
        }

        return colours
    }

    constructor(count, clock) {
        const position = new ChapterPositions(count)
        const geometry = new BufferGeometry()
        geometry.setAttribute('position', position.getVertexPositionsForChapter(count).clone(), 3)
        geometry.setAttribute('color', new Float32BufferAttribute(Vertices.#colours(count), 3))

        const material = new PointsMaterial({
            size: .4,
            sizeAttenuation: true,
            vertexColors: true,
            fog: false,
            opacity: .4,
            transparent: window.matchMedia('(prefers-color-scheme: dark)').matches
        })

        window.matchMedia('(prefers-color-scheme: dark)').addEventListener("change", ({ matches }) => {
            material.transparent = matches
            material.needsUpdate = true
        })

        super(geometry, material)

        this.#chapterPositions = position
        this.#clock = clock
        this.#count = count
    }

    /**
     * 
     * @param {number} vertex 
     * @param {import("three").Vector3} position 
     * @returns {import("three").Vector3}
     */
    #getTargetPosition(vertex, position) {
        return position ? this.#positionDifference(vertex, position) : this.#newPosition()
    }

    #moveVertexCooldown(vertex, edges, newPosition) {
        const ease = (cooldown, initialCooldown) =>
            this.#ease(cooldown / initialCooldown) - this.#ease(cooldown / initialCooldown - 1 / initialCooldown)

        return (cooldown, initialCooldown) => {
            const deltaEase = ease(cooldown, initialCooldown)
            const newFixture = this.#update(vertex,
                new Vector3(
                    newPosition.x * deltaEase,
                    newPosition.y * deltaEase,
                    newPosition.z * deltaEase,
                )
                // How would this actually work?
                // newPosition.multiplyScalar(deltaEase) 
            )
            edges.forEach(update => update(newFixture))
        }
    }

    getVertexAttribute(index, attribute) {
        const positionAttribute = this.geometry.getAttribute(attribute)

        return [
            positionAttribute.getX(index),
            positionAttribute.getY(index),
            positionAttribute.getZ(index)
        ]
    }

    moveVertex(vertex, edges, position = undefined, initialCooldown = undefined) {
        const newPosition = this.#getTargetPosition(vertex, position)

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            const newFixture = this.#update(vertex, newPosition)
            edges.forEach(update => update(newFixture))
        } else {
            this.#cooldowns[vertex]?.()
            this.#cooldowns[vertex] = this.#clock.cooldown(
                this.#moveVertexCooldown(vertex, edges, newPosition),
                initialCooldown
            )
        }
    }

    setCurrentChapter(id, edgesSet) {
        const positions = this.#chapterPositions
            .getVertexPositionsForChapter(this.#count, id)

        for (let vertex = 0; vertex < positions.count; vertex++) {
            const edges = edgesSet.get(vertex) ?? []
            const position = new Vector3(positions.getX(vertex), positions.getY(vertex), positions.getZ(vertex))

            this.moveVertex(vertex, edges, position, this.#clock.getCooldownFromSeconds(.4))
        }
    }
}